import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { addSalespersonAPI, deleteSalespersonAPI, getSalespersonsAPI, updateSalespersonAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import AddEditModal from './components/AddSalesModal';
import "./SalesPerson.css";

const SalesPerson = () => {
    const handleToggleVideoCall = async (person, e) => {
        e.stopPropagation();
        try {
            const newStatus = person?.is_busy == 1 ? 0 : 1;
            const response = await updateSalespersonAPI({
                sales_person_id: person?._id,
                is_busy: newStatus
            });

            if (response?.status === 200) {
                setSalesPersons(prevPersons => 
                    prevPersons.map(p => 
                        p._id === person._id 
                            ? { ...p, is_busy: newStatus } 
                            : p
                    )
                );
                showToast(`Video call status updated to ${newStatus === 1 ? 'Busy' : 'Available'}`, 'success');
            } else {
                showToast(response?.message || 'Failed to update video call status', 'error');
            }
        } catch (err) {
            console.log('Toggle error:', err);
            showToast('An error occurred. Please try again.', 'error');
        }
    };
    const [salesPersons, setSalesPersons] = useState([]);
    const [totalPagesServer, setTotalPagesServer] = useState(1);
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [personToDelete, setPersonToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);

    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [searchTerm, setSearchTerm] = useState("");

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    useEffect(() => {
        fetchSalesPersons();
    }, []);

    const fetchSalesPersons = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getSalespersonsAPI({
                page,
                limit: 10,
                search: normalizedSearch || "",
            });
            if (response?.status === 200) {
                const list = response.data;
                const totalPagesFromApi = response?.pagination?.total_pages || 1;
                const currentPageFromApi = response?.pagination?.current_page || page;
                setSalesPersons(list);
                setTotalPagesServer(totalPagesFromApi);
                if (currentPageFromApi !== page) setPage(currentPageFromApi);
            } else {
                setError(response?.message || 'Failed to fetch sales persons');
            }
        } catch (err) {
            console.log('Fetch error:', err);
            setError('Failed to fetch sales persons. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const normalizedSearch = searchTerm.toLowerCase().trim();
    const totalEntries = undefined;
    const totalPages = totalPagesServer;
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * 10;
    const endIdx = startIdx + salesPersons.length;

    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    useEffect(() => {
        fetchSalesPersons();
    }, [page, normalizedSearch]);

    const handleSort = (field) => {
        let newOrder = "asc";
        if (sortField === field) {
            newOrder = sortOrder === "asc" ? "desc" : "asc";
        }
        setSortField(field);
        setSortOrder(newOrder);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <FaSort />;
        }
        return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
    };

    const getSortedData = () => {
        if (!sortField || !sortOrder) {
            return salesPersons;
        }

        return [...salesPersons].sort((a, b) => {
            let aValue = a[sortField] || "";
            let bValue = b[sortField] || "";

            // Convert to lowercase for case-insensitive sorting
            aValue = aValue.toString().toLowerCase();
            bValue = bValue.toString().toLowerCase();

            if (sortOrder === "asc") {
                return aValue.localeCompare(bValue);
            } else {
                return bValue.localeCompare(aValue);
            }
        });
    };

    const getPageItems = () => {
        const items = [];
        const current = Math.max(1, Math.min(page, totalPages));
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) items.push(i);
            return items;
        }

        items.push(1);
        const showLeftDots = current > 4;
        const showRightDots = current < totalPages - 3;

        if (showLeftDots) items.push('dots-left');

        const start = Math.max(2, current - 1);
        const end = Math.min(totalPages - 1, current + 1);
        for (let i = start; i <= end; i++) items.push(i);

        if (showRightDots) items.push('dots-right');

        items.push(totalPages);
        return items;
    };

    const handleEdit = (person) => {
        setSelectedPerson(person);
        setName(person.name);
        setEmail(person.email);
        setPassword(person.password || "");
        setIsEditMode(true);
        setShowModal(true);
    };

    const handleAddNew = () => {
        setSelectedPerson(null);
        setName("");
        setEmail("");
        setPassword("");
        setIsEditMode(false);
        setShowModal(true);
        setSaveLoading(false);
    };

    const handleSave = async () => {
        if (!name.trim() || !email.trim()) {
            showToast("Please fill name and email fields.", "error");
            return;
        }

        if (!isEditMode && !password.trim()) {
            showToast("Please fill name, email and password fields.", "error");
            return;
        }

        if (password.trim() && password.trim().length < 8) {
            showToast("Password must be at least 8 characters long.", "error");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            showToast("Please enter a valid email address.", "error");
            return;
        }

        setSaveLoading(true);

        try {
            if (isEditMode && selectedPerson) {
                const reqData = {
                    sales_person_id: selectedPerson._id || selectedPerson.person_id,
                    name: name.trim(),
                    email: email.trim()
                };

                if (password.trim() && password.trim() !== selectedPerson.password) {
                    reqData.password = password.trim();
                }

                const response = await updateSalespersonAPI(reqData);

                if (response?.status === 200) {
                    await fetchSalesPersons();
                    setShowModal(false);
                    setName("");
                    setEmail("");
                    setPassword("");
                    setIsEditMode(false);
                    setSelectedPerson(null);
                    showToast("Sales Person updated successfully!", "success");
                } else {
                    showToast(response?.message || 'Failed to update sales person', "error");
                }
            } else {
                const reqData = {
                    name: name.trim(),
                    email: email.trim(),
                    password: password.trim()
                };

                const response = await addSalespersonAPI(reqData);

                if (response?.status === 200) {
                    await fetchSalesPersons();
                    setShowModal(false);
                    setName("");
                    setEmail("");
                    setPassword("");
                    setIsEditMode(false);
                    setSelectedPerson(null);
                    showToast("Sales Person added successfully!", "success");
                } else {
                    showToast(response?.message || 'Failed to add sales person', "error");
                }
            }
        } catch (err) {
            console.error('Save error:', err);
            showToast('An error occurred. Please try again.', "error");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setName("");
        setEmail("");
        setPassword("");
        setIsEditMode(false);
        setSelectedPerson(null);
        setSaveLoading(false);
    };

    const handleDeleteClick = (person) => {
        setPersonToDelete(person);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {

            if (!personToDelete?._id) {
                showToast("Invalid salesperson data", "error");
                return;
            }

            const reqData = {
                sales_person_id: personToDelete._id
            };

            const response = await deleteSalespersonAPI(reqData);

            if (response?.status === 200) {
                setShowDeleteModal(false);
                setPersonToDelete(null);
                showToast(`Sales Person "${personToDelete?.name}" deleted successfully!`, "success");
                await fetchSalesPersons();
            } else {
                showToast(response?.message || 'Failed to delete sales person', "error");
            }
        } catch (err) {
            console.log('Delete error:', err);
            showToast('An error occurred. Please try again.', "error");
        } finally {
            // no-op
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setPersonToDelete(null);
    };



    // Handle action buttons click (edit/delete)
    const handleActionClick = (e, action, person) => {
        e.stopPropagation();
        if (action === 'videocall') {
            handleToggleVideoCall(person, e);
        } else if (action === 'edit') {
            handleEdit(person);
        } else if (action === 'delete') {
            handleDeleteClick(person);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    };

    const sortedSalesPersons = getSortedData();

    return (
        <>
            <Toast toast={toast} />

            <div className="SalesPerson-layout">
                <Sidebar />
                <div className="SalesPerson-main-content">
                    <div className="SalesPerson-header-row">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name or email"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Search sales persons"
                        />
                        <button
                            type="button"
                            className="insert-btn"
                            onClick={handleAddNew}
                            aria-label="Add Sales Person"
                        >
                            <span>Add Sales Person</span>
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Loading salespersons...</p>
                        </div>
                    ) : error ? (
                        <div className="loading-container">
                            <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
                            <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
                        </div>
                    ) : searchTerm === "" && salesPersons.length === 0 ? (
                        <div className="empty-state">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                            <h3>No Sales Persons Found</h3>
                            <p>There are no sales persons in the system yet.</p>
                            <p>Click "Add Sales Person" to create the first one.</p>
                        </div>
                    ) : salesPersons.length === 0 ? (
                        <div className="empty-state">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
                            <h3>No results</h3>
                            <p>We couldn't find any sales person matching "{searchTerm}".</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="SalesPerson-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Video Call</th>
                                            <th className="actions-col">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {salesPersons.map((person, idx) => (
                                            <tr key={person._id || `person-${idx}`}>
                                                <td>{startIdx + idx + 1}</td>
                                                <td>{person.name || ''}</td>
                                                <td>{person.email || ''}</td>
                                                <td>
                                                    <label className="sales-toggle-switch" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={person?.is_busy === 0}
                                                            onChange={(e) => handleToggleVideoCall(person, e)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <span className="sales-toggle-slider"></span>
                                                    </label>
                                                </td>
                                                <td>
                                                    <div className="sales-action-btn-row" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            className="sales-action-btn sales-edit-btn"
                                                            title="Edit Sales Person"
                                                            onClick={(e) => handleActionClick(e, 'edit', person)}
                                                            aria-label="Edit"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="sales-action-btn sales-delete-btn"
                                                            title="Delete Sales Person"
                                                            onClick={(e) => handleActionClick(e, 'delete', person)}
                                                            aria-label="Delete"
                                                        >
                                                            <MdDelete />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="table-footer">
                                <div className="footer-left">
                                    <span>
                                        Showing {salesPersons.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                                    </span>
                                </div>
                                <div className="pagination">
                                    <button
                                        type="button"
                                        disabled={page === 1}
                                        onClick={() => handlePageChange(page - 1)}
                                        aria-label="Previous page"
                                    >
                                        Previous
                                    </button>
                                    {getPageItems().map((item, idx) => (
                                        typeof item === 'number' ? (
                                            <button
                                                type="button"
                                                key={`p-${item}`}
                                                className={page === item ? 'active' : ''}
                                                onClick={() => handlePageChange(item)}
                                                aria-current={page === item ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        ) : (
                                            <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                                        )
                                    ))}
                                    <button
                                        type="button"
                                        disabled={page == totalPages}
                                        onClick={() => handlePageChange(page + 1)}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    <AddEditModal
                        showModal={showModal}
                        isEditMode={isEditMode}
                        name={name}
                        setName={setName}
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        saveLoading={saveLoading}
                        onSave={handleSave}
                        onClose={handleCloseModal}
                    />

                    <ConfirmationModal
                        visible={showDeleteModal}
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                        title="Confirm Deletion"
                        message={`Are you sure you want to delete the Sales Person "${personToDelete?.name || ''}"?`}
                        confirmText="Delete"
                    />
                </div>
            </div>
        </>
    );
};

export default SalesPerson; 